import { ScanBarcode, CheckCircle, ArrowUpFromLine, ClipboardCheck, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import Navigation from "@/components/Navigation";
import { useAuth } from "@/context/AuthContext";
import { useStocktaking } from "@/context/StocktakingContext";
import { useScanPage } from "@/hooks/useScanPage";
import {
  CameraScanner,
  ScanActionDialog,
  NewProductDialog,
  RecentScansList,
  LastScannedCard,
  InventoryCompletedView,
  InventoryLivePanel,
  EanInput,
  ScanWizard,
  BookingSelectionDialog,
  DeliveryTypeDialog,
  BookingBlockDialog,
  ZeroStockDialog,
} from "@/components/scan";

export default function ScanPage() {
  const { user } = useAuth();
  const { startStocktaking, finishStocktaking } = useStocktaking();
  const scan = useScanPage(user, { startStocktaking, finishStocktaking });

  return (
    <div className="min-h-screen bg-background pb-20 md:pb-0">
      <Navigation />
      
      {/* Header */}
      <header className="border-b border-border bg-card">
        <div className="container mx-auto px-4 py-4">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-3 min-w-0">
              {scan.inventoryMode || scan.inventoryCompleted ? (
                <ClipboardCheck className="h-6 w-6 text-blue-500 shrink-0" />
              ) : scan.scanMode === "out" ? (
                <ArrowUpFromLine className="h-6 w-6 text-red-500 shrink-0" />
              ) : (
                <ScanBarcode className="h-6 w-6 text-primary shrink-0" />
              )}
              <h1 className="font-heading text-xl sm:text-2xl font-bold uppercase tracking-tight" data-testid="scan-title">
                {scan.inventoryMode ? "Inventering" : scan.inventoryCompleted ? "Inventering slutförd" : scan.scanMode === "out" ? "Utleverans" : "Inleverans"}
              </h1>
              {scan.inventoryMode && (
                <Badge variant="outline" className="text-blue-500 border-blue-500/50 bg-blue-500/10 font-mono truncate max-w-[40vw]">
                  {scan.lagerplats || scan.inventoryLocation}
                </Badge>
              )}
              {scan.scanMode === "out" && !scan.inventoryMode && (
                <Badge variant="outline" className="text-red-500 border-red-500/50 bg-red-500/10">
                  Utleverans
                </Badge>
              )}
            </div>
            {scan.inventoryMode && (
              <Button
                variant="outline"
                className="border-green-500/50 text-green-500 hover:bg-green-500/10 w-full sm:w-auto shrink-0"
                onClick={scan.handleFinishInventory}
                data-testid="finish-inventory-btn"
              >
                <CheckCircle className="h-4 w-4 mr-2" />
                Slutför inventering
              </Button>
            )}
          </div>
          {scan.inventoryMode && (
            <p className="text-sm text-muted-foreground mt-1">
              Skanningar loggas som inventering (påverkar ej statistik)
            </p>
          )}
        </div>
      </header>

      <main className="container mx-auto px-4 py-6">
        {/* Inventory Completed View */}
        {scan.inventoryCompleted && (
          <InventoryCompletedView
            location={scan.completedLocation}
            products={scan.locationProducts}
            loading={scan.loadingProducts}
            onNewInventory={scan.handleNewInventory}
          />
        )}
        
        {/* Normal Scan View */}
        {!scan.inventoryCompleted && (
          <div className="max-w-xl mx-auto space-y-4">
            
            {/* Camera Scanner */}
            {scan.showCameraScanner && !scan.scannedProduct && (
              <Card className="mb-4" data-testid="camera-scanner">
                <CardContent className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <ScanBarcode className="h-5 w-5 text-primary" />
                      <span className="font-medium">Kameraskanning</span>
                    </div>
                    <Button variant="ghost" size="sm" onClick={scan.stopCameraScanner}>
                      Stäng
                    </Button>
                  </div>
                  
                  {scan.cameraLoading && (
                    <div className="flex items-center justify-center p-8">
                      <Loader2 className="h-8 w-8 animate-spin text-primary" />
                      <span className="ml-2">Startar kamera...</span>
                    </div>
                  )}
                  
                  {scan.cameraError && (
                    <div className="text-destructive text-sm p-4 bg-destructive/10 rounded-lg">
                      {scan.cameraError}
                    </div>
                  )}
                  
                  <div 
                    id={scan.scannerContainerId}
                    className="relative w-full aspect-video bg-black rounded-lg overflow-hidden"
                    style={{ minHeight: "200px" }}
                  />
                  
                  <p className="text-xs text-muted-foreground text-center mt-2">
                    Rikta kameran mot streckkoden
                  </p>
                </CardContent>
              </Card>
            )}
            
            {/* EAN Input - only show when no product scanned */}
            {!scan.scannedProduct && (
              <EanInput
                ref={scan.eanInputRef}
                value={scan.ean}
                onChange={scan.setEan}
                onSubmit={() => scan.handleEanLookup()}
                loading={scan.lookingUp}
                disabled={!!scan.scannedProduct}
                onCameraClick={scan.showCameraScanner ? scan.stopCameraScanner : scan.startCameraScanner}
                showCameraButton={!scan.scannedProduct}
              />
            )}
            
            {/* Scan Wizard - Step by step after product is found */}
            {scan.scannedProduct && (
              <ScanWizard
                product={scan.scannedProduct}
                productLocations={scan.productLocations}
                locations={scan.locations}
                recentLocations={scan.recentLocations}
                scanMode={scan.scanMode}
                inventoryMode={scan.inventoryMode}
                inventoryLocation={scan.inventoryLocation}
                lagerplats={scan.lagerplats}
                setLagerplats={scan.setLagerplats}
                antal={scan.antal}
                setAntal={scan.setAntal}
                onSubmit={scan.handleScanSubmit}
                onCancel={scan.handleClearProduct}
                processing={scan.processing}
                locationInputRef={scan.locationInputRef}
              />
            )}
            
            {/* Last Scanned */}
            {!scan.inventoryMode && <LastScannedCard lastScanned={scan.lastScanned} />}
            
            {/* Recent Scans (normal mode) / Live count panel (inventory mode) */}
            {scan.inventoryMode ? (
              <InventoryLivePanel
                location={scan.lagerplats || scan.inventoryLocation}
                items={scan.inventoryList}
                loading={scan.inventoryListLoading}
                lastUpdate={scan.lastInventoryUpdate}
              />
            ) : (
              <RecentScansList scans={scan.recentScans} />
            )}
          </div>
        )}
      </main>

      {/* Scan Action Dialog */}
      <ScanActionDialog
        open={scan.showScanActionDialog}
        onOpenChange={(open) => {
          if (!open) scan.handleCancelScanAction();
        }}
        product={scan.pendingProduct}
        locations={scan.pendingLocations}
        onScanIn={() => scan.handleScanActionChoice("in")}
        onScanOut={() => scan.handleScanActionChoice("out")}
        onMove={() => scan.handleMoveChoice()}
        processing={scan.processing}
      />

      {/* Booking Selection Dialog - shown when scanning out a booked product */}
      <BookingSelectionDialog
        open={scan.showBookingDialog}
        onOpenChange={scan.setShowBookingDialog}
        bookings={scan.productBookings}
        onSelectBooking={scan.handleBookingChoice}
        onSkip={scan.handleSkipBooking}
      />

      {/* New Product Dialog */}
      <NewProductDialog
        open={scan.showNewProductDialog}
        onOpenChange={scan.setShowNewProductDialog}
        newProduct={scan.newProduct}
        onProductChange={scan.setNewProduct}
        manufacturers={scan.manufacturers}
        categories={scan.categories}
        onSave={scan.handleSaveNewProduct}
        processing={scan.processing}
      />
      
      {/* Delivery Type Dialog - shown when scanning out (Fas 3) */}
      <DeliveryTypeDialog
        open={scan.showDeliveryTypeDialog}
        onOpenChange={scan.setShowDeliveryTypeDialog}
        onSelect={scan.handleDeliveryTypeSelect}
        product={scan.pendingDeliveryData?.product || scan.scannedProduct}
        currentStock={scan.currentStockForDelivery}
        bookedQuantity={scan.bookedQuantityForDelivery}
        hasBookings={scan.blockedBookings?.length > 0}
      />
      
      {/* Zero Stock Dialog - shown when trying to scan out with 0 stock */}
      <ZeroStockDialog
        open={scan.showZeroStockDialog}
        onOpenChange={scan.setShowZeroStockDialog}
        product={scan.scannedProduct}
        location={scan.lagerplats}
      />
      
      {/* Booking Block Dialog - shown when all stock is booked */}
      <BookingBlockDialog
        open={scan.showBookingBlockDialog}
        onOpenChange={scan.setShowBookingBlockDialog}
        product={scan.scannedProduct}
        bookings={scan.blockedBookings}
        onCancelBooking={scan.handleCancelBooking}
      />
    </div>
  );
}
